import { useCallback, useEffect, useState } from 'react'

import { Box, Paper, Typography } from '@material-ui/core'
import { MenuOpen as ExpandMoreIcon } from '@material-ui/icons'
import {
  AppMethodLabels,
  AppMethodType,
  ApplicantType,
  ApplicantTypeLabels,
  ApplicationStatusLabels,
  ApplicationStatusType,
} from '@scoir/application-info'
import Table, { SortDirection } from 'material/MuiTable'
import useTableContext from 'material/MuiTable/use-table-context'

import api from 'common/api'
import useQuery from 'common/use-query-params'
import { FirstLastNameSearch } from 'components/FirstLastNameSearch'
import { useHistory } from 'components/ReactRouterProvider/context'
import { DrawerTrigger } from 'routes/application/Applicants/FocusedApplicantDrawer'
import { useCanSortApplicationInsights } from 'store/feature-toggle/hooks'
import { useScid } from 'store/user/hooks'

type applicantRecord = {
  Id: string
  FirstName: string
  LastName: string
  HighSchoolName: string
  AppMethod: AppMethodType
  ApplicantType?: ApplicantType
  ApplicationStatus?: ApplicationStatusType
  DidNotAttendHighSchool?: boolean
  Type: string
}

type payload = {
  Total: number
  // TODO: we don't want null to ever occur, need the API to not allow that
  Results: applicantRecord[] | null
}

const ApplicantsTable = ({ scoirApplyOnly }: { scoirApplyOnly: boolean }) => {
  const canSort = useCanSortApplicationInsights()
  const [results, setResults] = useState<applicantRecord[]>([])
  const [hasError, setHasError] = useState(false)
  const [total, setTotal] = useState(0)
  const [refresh, setRefresh] = useState(false)

  const history = useHistory()
  const params = useQuery()
  const { page, pageSize, sort, sortDir, updateContext } = useTableContext({
    defaultSortKey: 'LastName',
    defaultSortDir: 'asc',
  })
  const paramFirstName = params.get('firstname')
  const paramLastName = params.get('lastname')
  const scid = useScid()
  const fetchResults = useCallback(
    (ctx: {
      page: number
      pageSize: number
      sort: string
      sortDir: SortDirection
      firstName: string | null
      lastName: string | null
    }) => {
      const queryParams: Record<string, string> = {
        sort: ctx.sortDir === 'desc' ? `-${ctx.sort}` : ctx.sort,
        from: `${ctx.page * ctx.pageSize}`,
        pageSize: `${ctx.pageSize}`,
      }
      if (!canSort) {
        delete queryParams.sort
      }
      if (scoirApplyOnly) {
        queryParams.scoirApplyOnly = 'true'
      }
      if (ctx.firstName) {
        queryParams.firstname = ctx.firstName
      }
      if (ctx.lastName) {
        queryParams.lastname = ctx.lastName
      }
      if (refresh) {
        queryParams.from = '0'
      }
      const params = new URLSearchParams(queryParams)
      setHasError(false)
      return api
        .get<payload>(`/api/colleges/${scid}/applicants/search?${params.toString()}`)
        .then((results) => {
          setTotal(results.Total)
          setResults(results.Results ?? [])
        })
        .catch(() => {
          setHasError(true)
        })
    },
    [scoirApplyOnly, scid, canSort],
  )
  useEffect(() => {
    if (refresh) {
      updateContext({ page: 0 })
      setRefresh(false)
    } else {
      fetchResults({
        page,
        pageSize,
        sort,
        sortDir,
        firstName: paramFirstName,
        lastName: paramLastName,
      })
    }
  }, [fetchResults, sort, sortDir, pageSize, page, paramFirstName, paramLastName, refresh])
  const handleSearch = useCallback(
    (firstName?: string, lastName?: string) => {
      params.delete('firstname')
      params.delete('lastname')
      setRefresh(true)

      if (firstName) {
        params.append('firstname', firstName)
      }
      if (lastName) {
        params.append('lastname', lastName)
      }
      history.replace({ search: params.toString() })
    },
    [history, params],
  )
  return (
    <Paper>
      <Box
        p={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
      >
        <Box
          my={1}
          mr={1}
        >
          <Typography component="h3">Applications</Typography>
        </Box>
        <FirstLastNameSearch
          firstName={paramFirstName ?? ''}
          lastName={paramLastName ?? ''}
          onSubmit={handleSearch}
        />
      </Box>
      <Table
        keyBy="Id"
        columns={[
          {
            id: 'LastName',
            label: 'Student Name',
            render: ({ FirstName, LastName }) => `${LastName}, ${FirstName}`,
            sortKey: canSort ? 'LastName' : undefined,
          },
          {
            id: 'HighSchool',
            label: 'High School',
            render: ({ HighSchoolName, DidNotAttendHighSchool }) =>
              HighSchoolName && !DidNotAttendHighSchool ? HighSchoolName : 'N/A',
          },
          {
            id: 'ApplicantType',
            label: 'Applicant Type',
            render: ({ ApplicantType }) => ApplicantType && ApplicantTypeLabels[ApplicantType],
            // sortKey: 'ApplicantType',
            hidden: !scoirApplyOnly,
          },
          {
            id: 'AppMethod',
            label: 'App Method',
            render: ({ AppMethod }) => AppMethodLabels[AppMethod],
            // sortKey: 'AppMethod',
          },
          {
            id: 'AppType',
            label: 'App Round',
            render: ({ Type }) => Type,
            // sortKey: 'Type',
          },
          {
            id: 'ApplicationStatus',
            label: 'Application Status',
            render: ({ ApplicationStatus }) => ApplicationStatus && ApplicationStatusLabels[ApplicationStatus],
            hidden: !scoirApplyOnly,
          },
          {
            id: 'Expand',
            label: 'Actions',
            render: ({ Id, FirstName, LastName }) => (
              <DrawerTrigger
                ariaLabel={`expand applicant record for ${FirstName} ${LastName}`}
                applicantId={Id}
              >
                <ExpandMoreIcon />
              </DrawerTrigger>
            ),
          },
        ]}
        records={results}
        total={total}
        order={sortDir}
        orderBy={sort}
        page={page}
        rowsPerPage={pageSize}
        onSortChange={(sort, sortDir) =>
          updateContext({
            page: 0,
            sort,
            sortDir,
            pageSize,
          })
        }
        onPageSizeChange={(pageSize) =>
          updateContext({
            page: 0,
            pageSize,
          })
        }
        onPageChange={(page) => updateContext({ page })}
      />
      {hasError && (
        <Box
          mt={1}
          pb={2}
        >
          <Typography
            role="alert"
            color="error"
            align="center"
          >
            An error occurred while loading the applicants
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default ApplicantsTable
